import { createAction, props } from '@ngrx/store';

import {
    BackupPaymentMethod,
    BackupPaymentMethodToken,
    PaymentSession,
} from '../../interfaces/payment-method';

/**
 * Backup payment method actions (OPS-2028).
 */
export enum PaymentMethodActionsIds {
    LoadBackupMethod = '[Payment Method] Load backup method',
    LoadBackupMethodSuccess = '[Payment Method] Load backup method success',
    LoadBackupMethodError = '[Payment Method] Load backup method error',
    OpenTokenizeSession = '[Payment Method] Open tokenize session',
    OpenTokenizeSessionSuccess = '[Payment Method] Open tokenize session success',
    OpenTokenizeSessionError = '[Payment Method] Open tokenize session error',
    SaveBackupMethod = '[Payment Method] Save backup method',
    SaveBackupMethodSuccess = '[Payment Method] Save backup method success',
    SaveBackupMethodError = '[Payment Method] Save backup method error',
    ClearPaymentMethodError = '[Payment Method] Clear error',
    ResetPaymentMethodState = '[Payment Method] Reset state',
}

/**
 * Reads the card on file for one lease, with the masked details the modal shows. The lease
 * list already says whether one exists; this is for the screen that has to render it.
 */
export const loadBackupMethod = createAction(
    PaymentMethodActionsIds.LoadBackupMethod,
    props<{ leaseKey: string }>()
);

export const loadBackupMethodSuccess = createAction(
    PaymentMethodActionsIds.LoadBackupMethodSuccess,
    props<{ leaseKey: string; backupMethod: BackupPaymentMethod | null }>()
);

export const loadBackupMethodError = createAction(
    PaymentMethodActionsIds.LoadBackupMethodError,
    props<{ error: string }>()
);

export const openTokenizeSession = createAction(
    PaymentMethodActionsIds.OpenTokenizeSession,
    props<{ leaseKey: string }>()
);

export const openTokenizeSessionSuccess = createAction(
    PaymentMethodActionsIds.OpenTokenizeSessionSuccess,
    props<{ session: PaymentSession }>()
);

export const openTokenizeSessionError = createAction(
    PaymentMethodActionsIds.OpenTokenizeSessionError,
    props<{ error: string }>()
);

/**
 * Saves the tokenized method and, when the ROSE ticked the authorization box, records that
 * acknowledgement too. The description is the exact wording she was shown.
 */
export const saveBackupMethod = createAction(
    PaymentMethodActionsIds.SaveBackupMethod,
    props<{ leaseKey: string; token: BackupPaymentMethodToken; authorizationDescription: string }>()
);

export const saveBackupMethodSuccess = createAction(
    PaymentMethodActionsIds.SaveBackupMethodSuccess,
    props<{ leaseKey: string; backupMethod: BackupPaymentMethod }>()
);

export const saveBackupMethodError = createAction(
    PaymentMethodActionsIds.SaveBackupMethodError,
    props<{ error: string }>()
);

/**
 * Retires a failure the ROSE has already been shown and moved on from.
 *
 * The error is one field shared by every path in this slice, so a failure left sitting there
 * after its screen is gone is not history — it is a message still waiting to be reported by
 * whoever reads the field next. Leaving a panel is the moment it stops being current.
 */
export const clearPaymentMethodError = createAction(PaymentMethodActionsIds.ClearPaymentMethodError);

export const resetPaymentMethodState = createAction(PaymentMethodActionsIds.ResetPaymentMethodState);
